import { useState, useEffect, useRef } from 'react'
import {
    Button, Dialog, DialogContent, DialogActions,
    DialogTitle, IconButton, Paper, CircularProgress,
    Alert, AlertTitle, Box
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import CloseIcon from '@mui/icons-material/Close'
import { editInterpretationRule, gettingInterpretationRuleById } from 'utils/InterpretationPunchRule'
import Draggable from 'react-draggable'
import { nameValidation, normalizeString } from 'utils/Validations'
import { InterpretationRuleForm } from 'components/dialog/interpretation_rules/InterpretationRuleForm'
import type { InterpretationRuleDurations, InterpretationRuleFormData } from 'components/dialog/interpretation_rules/InterpretationRuleForm'

interface InterpretationRuleRow {
    id: number
    name: string
}

interface EditInterpretationRuleProps {
    open: boolean
    setOpen: (value: boolean) => void
    rows: InterpretationRuleRow[]
    reloading: () => void
    selectedRow: InterpretationRuleRow[]
    cleanSelected: () => void
}

const PaperComponent = (props: any) => {
    const nodeRef = useRef<HTMLDivElement | null>(null)
    return (
        <Draggable
            nodeRef={nodeRef}
            handle='#draggable-dialog-title'
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    )
}

const defaultDurations: InterpretationRuleDurations = {
    early_start_margin: '04:00',
    early_start_restriction: '24:00',
    late_start_restriction: '24:00',
    late_start_margin: '04:00',
    omitted_output_margin: '01:00',
    brake_maximum_output: '00:30',
    brake_minimum_refreshment_: '00:30',
    brake_early_rest_start: '00:15',
    brake_late_rest_start_margin: '00:15',
    brake_late_rest_end_margin: '00:15',
    start_early_end_restriction: '24:00',
    end_early_end_restriction: '24:00',
    late_end_restriction: '24:00',
    punch_out_limit_omitted: '16:30',
    unplanned_shift_duration: '08:00',
}

const EditInterpretationRule = ({ open, setOpen, rows, reloading, selectedRow, cleanSelected }: EditInterpretationRuleProps) => {

    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [punch_restriction, setPunchRestriction] = useState('')
    const [allow_substitution, setAllowSubstitution] = useState<boolean | null>(null)
    const [brake_impose_reset, setBrakeImposeReset] = useState<boolean | null>(null)
    const [restrict_unplanned_punch_in, setRestrictUnplannedPunchIn] = useState<boolean | null>(null)
    const [loading, setLoading] = useState(false)
    const [alert, setAlert] = useState(false)

    const [showError, setShowError] = useState<Partial<Record<keyof InterpretationRuleDurations, boolean>>>({
        early_start_margin: false,
        early_start_restriction: false,
        late_start_restriction: false,
        late_start_margin: false,
        omitted_output_margin: false,
        brake_maximum_output: false,
        brake_minimum_refreshment_: false,
        brake_early_rest_start: false,
        brake_late_rest_start_margin: false,
        brake_late_rest_end_margin: false,
        start_early_end_restriction: false,
        end_early_end_restriction: false,
        late_end_restriction: false,
        punch_out_limit_omitted: false,
        unplanned_shift_duration: false,
    })

    const [durations, setDurations] = useState<InterpretationRuleDurations>(defaultDurations)

    useEffect(() => {
        if (open && selectedRow?.length > 0) {
            setLoading(true)
            gettingInterpretationRuleById(setLoading, selectedRow[0].id)
                .then((response: any) => {
                    setName(response.name)
                    setDescription(response.description)
                    setPunchRestriction(response.punch_restriction)
                    setAllowSubstitution(response.allow_substitution)
                    setBrakeImposeReset(response.brake_impose_reset ?? false)
                    setRestrictUnplannedPunchIn(response.restrict_unplanned_punch_in ?? false)
                    setDurations({
                        early_start_margin: response.early_start_margin,
                        early_start_restriction: response.early_start_restriction,
                        late_start_restriction: response.late_start_restriction,
                        late_start_margin: response.late_start_margin,
                        omitted_output_margin: response.omitted_output_margin,
                        brake_maximum_output: response.brake_maximum_output,
                        brake_minimum_refreshment_: response.brake_minimum_refreshment_,
                        brake_early_rest_start: response.brake_early_rest_start,
                        brake_late_rest_start_margin: response.brake_late_rest_start_margin,
                        brake_late_rest_end_margin: response.brake_late_rest_end_margin,
                        start_early_end_restriction: response.start_early_end_restriction,
                        end_early_end_restriction: response.end_early_end_restriction,
                        late_end_restriction: response.late_end_restriction,
                        punch_out_limit_omitted: response.punch_out_limit_omitted,
                        unplanned_shift_duration: response.unplanned_shift_duration,
                    })
                })
                .catch((error: unknown) => {
                    console.error('Error:', error)
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }, [open, selectedRow])

    const closeForm = () => {
        setName('')
        setDescription('')
        setPunchRestriction('complete')
        setAllowSubstitution(true)
        setBrakeImposeReset(false)
        setRestrictUnplannedPunchIn(false)
        setDurations(defaultDurations)
        setShowError({})
        setLoading(false)
        setOpen(false)
        setAlert(false)
        cleanSelected()
    }

    const existName = (currentName: string) => {
        const normalizedName = normalizeString(currentName)
        const selected = rows.find((row) => row.id === selectedRow[0]?.id)

        if (selected && normalizeString(selected.name) === normalizedName) {
            return false
        }

        return rows.some((row) => normalizeString(row.name) === normalizedName)
    }

    const changes = () => {
        const hasChanges = name !== '' ||
            description !== '' ||
            punch_restriction !== 'complete' ||
            allow_substitution !== true ||
            durations.early_start_margin !== '04:00' ||
            durations.early_start_restriction !== '24:00' ||
            durations.late_start_restriction !== '24:00' ||
            durations.late_start_margin !== '04:00' ||
            durations.omitted_output_margin !== '01:00' ||
            durations.brake_maximum_output !== '00:30' ||
            durations.brake_minimum_refreshment_ !== '00:30' ||
            brake_impose_reset !== false ||
            durations.brake_early_rest_start !== '00:15' ||
            durations.brake_late_rest_start_margin !== '00:15' ||
            durations.brake_late_rest_end_margin !== '00:15' ||
            durations.start_early_end_restriction !== '24:00' ||
            durations.end_early_end_restriction !== '24:00' ||
            durations.late_end_restriction !== '24:00' ||
            durations.punch_out_limit_omitted !== '16:30' ||
            restrict_unplanned_punch_in !== false ||
            durations.unplanned_shift_duration !== '08:00'
        return hasChanges ? setAlert(true) : closeForm()
    }

    const disabled = () => {
        return name === '' || existName(name)
            || showError.early_start_margin
            || showError.early_start_restriction
            || (punch_restriction === 'complete' && showError.late_start_restriction)
            || showError.late_start_margin
            || showError.omitted_output_margin
            || (punch_restriction === 'complete' && showError.brake_maximum_output)
            || (punch_restriction === 'complete' && showError.brake_minimum_refreshment_)
            || (punch_restriction === 'complete' && showError.brake_early_rest_start)
            || (punch_restriction === 'complete' && showError.brake_late_rest_start_margin)
            || (punch_restriction === 'complete' && showError.brake_late_rest_end_margin)
            || (punch_restriction === 'complete' && showError.start_early_end_restriction)
            || (punch_restriction === 'complete' && showError.end_early_end_restriction)
            || showError.late_end_restriction
            || showError.punch_out_limit_omitted
            || (punch_restriction === 'complete' && showError.restrict_unplanned_punch_in)
            || showError.unplanned_shift_duration
    }

    const EditInterpretationRules = async () => {
        try {
            const body = {
                name,
                description,
                punch_restriction,
                allow_substitution,
                early_start_margin: durations.early_start_margin,
                early_start_restriction: durations.early_start_restriction,
                late_start_restriction: punch_restriction === 'complete' ? durations.late_start_restriction : null,
                late_start_margin: durations.late_start_margin,
                omitted_output_margin: durations.omitted_output_margin,
                brake_maximum_output: punch_restriction === 'complete' ? durations.brake_maximum_output : null,
                brake_minimum_refreshment_: punch_restriction === 'complete' ? durations.brake_minimum_refreshment_ : null,
                brake_impose_reset: punch_restriction === 'complete' ? brake_impose_reset : null,
                brake_early_rest_start: punch_restriction === 'complete' ? durations.brake_early_rest_start : null,
                brake_late_rest_start_margin: punch_restriction === 'complete' ? durations.brake_late_rest_start_margin : null,
                brake_late_rest_end_margin: punch_restriction === 'complete' ? durations.brake_late_rest_end_margin : null,
                start_early_end_restriction: punch_restriction === 'complete' ? durations.start_early_end_restriction : null,
                end_early_end_restriction: punch_restriction === 'complete' ? durations.end_early_end_restriction : null,
                late_end_restriction: durations.late_end_restriction,
                punch_out_limit_omitted: durations.punch_out_limit_omitted,
                restrict_unplanned_punch_in: punch_restriction === 'complete' ? restrict_unplanned_punch_in : null,
                unplanned_shift_duration: durations.unplanned_shift_duration,
                enable: true,
            }

            const response = await editInterpretationRule(setLoading, selectedRow[0].id, body)
            if ([200, 201].includes(response.status)) {
                enqueueSnackbar(t('settings.interpretation_rule.dialog.alerts.edit.success'), { variant: 'success' })
                reloading()
                closeForm()
            }

        } catch (error) {
            console.error('Error:', error)
            enqueueSnackbar(t('settings.interpretation_rule.dialog.inputs.messages.error'), { variant: 'error' })
        }
    }

    const formData: InterpretationRuleFormData = {
        name,
        description,
        punch_restriction,
        allow_substitution,
        brake_impose_reset,
        restrict_unplanned_punch_in,
        durations,
    }

    useEffect(() => {
        if (!open) {
            return
        }

        const timeout = setTimeout(() => {
            const nuevosErrores: Partial<Record<keyof InterpretationRuleDurations, boolean>> = {}
            for (const [key, value] of Object.entries(durations) as Array<[keyof InterpretationRuleDurations, string]>) {
                if (value?.length >= 0 && value.length < 5) {
                    nuevosErrores[key] = true
                }
            }
            setShowError(nuevosErrores)
        }, 500)

        return () => clearTimeout(timeout)
    }, [durations, open])

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return
                }
                changes()
            }}
            maxWidth='sm'
            fullWidth
            PaperComponent={PaperComponent}
            aria-labelledby='draggable-dialog-title'
            aria-describedby='draggable-alert-dialog-description'
            aria-hidden={!open}
        >
            <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>{t('settings.interpretation_rule.dialog.titles.edit')}</DialogTitle>
            <IconButton
                aria-label='close'
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {
                    alert &&
                    <Alert
                        variant='filled'
                        severity='warning'
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlert(false)} color='inherit' size='small' variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>
                                <Button onClick={() => closeForm()} color='inherit' size='small' variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                }
            </Box>
            <DialogContent dividers>
                {
                    loading ? (
                        <Box sx={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <InterpretationRuleForm
                            formData={formData}
                            showError={showError}
                            nameErrorText={existName(name) ? t('settings.interpretation_rule.dialog.inputs.exist_name') : ''}
                            onSubmit={EditInterpretationRules}
                            onNameChange={(value) => {
                                if (nameValidation(value)) {
                                    setName(value)
                                }
                            }}
                            onDescriptionChange={setDescription}
                            onPunchRestrictionChange={setPunchRestriction}
                            onAllowSubstitutionChange={setAllowSubstitution}
                            onBrakeImposeResetChange={setBrakeImposeReset}
                            onRestrictUnplannedPunchInChange={setRestrictUnplannedPunchIn}
                            onDurationChange={(key, value) =>
                                setDurations((prev) => ({
                                    ...prev,
                                    [key]: value,
                                }))
                            }
                        />
                    )
                }

            </DialogContent>
            <DialogActions>
                <Button onClick={() => changes()} variant='contained' color='error'>{t('general.buttons.cancel')}</Button>
                <Button disabled={disabled()} loading={loading} onClick={() => EditInterpretationRules()} variant='contained' color='primary'>{t('general.buttons.save')}</Button>
            </DialogActions>
        </Dialog>
    )
}

export { EditInterpretationRule }
